<template>
  <div class="iv-host">
    <div @click.stop="onThumbClick" class="iv-thumb">
      <slot></slot>
    </div>

    <transition name="iv-fade">
      <div v-if="visible" class="iv-overlay" @click.self="close">
        <div class="iv-backdrop" :style="backdropStyle"></div>

        <div class="iv-stage" ref="stage">
          <img
            ref="img"
            :src="src"
            :alt="alt"
            class="iv-image"
            :style="imageStyle"
            @load="onImageLoad"
            @dblclick.prevent="toggleZoom"
            @wheel.prevent="onWheel"
            @mousedown.prevent="startPan"
            @touchstart.passive="onTouchStart"
            @touchmove.passive="onTouchMove"
            @touchend.passive="onTouchEnd"
          />
          <button class="iv-close" @click.stop="close">✕</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ImageViewer',
  props: {
    src: { type: String, required: true },
    alt: { type: String, default: '' },
    maxZoom: { type: Number, default: 3 }
  },
  data() {
    return {
      visible: false,
      loaded: false,
      startRect: null,
      scale: 1,
      x: 0,
      y: 0,
      isPanning: false,
      lastPointer: null,
      pinchStartDist: 0,
      pinchStartScale: 1
    };
  },
  computed: {
    imageStyle() {
      const transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
      return { transform };
    },
    backdropStyle() {
      return { opacity: this.visible ? 1 : 0 };
    }
  },
  methods: {
    onThumbClick(e) {
      // compute thumbnail rect for animation
      const el = e.currentTarget.querySelector('img') || e.currentTarget;
      this.startRect = el.getBoundingClientRect();
      this.open();
    },
    open() {
      this.visible = true;
      this.$nextTick(() => {
        this.playOpenAnimation();
      });
    },
    close() {
      this.playCloseAnimation();
    },
    onImageLoad() { this.loaded = true; },
    playOpenAnimation() {
      const img = this.$refs.img;
      const stage = this.$refs.stage;
      if (!this.startRect || !img || !stage) return;

      const viewW = window.innerWidth;
      const viewH = window.innerHeight;
      const naturalW = img.naturalWidth || viewW;
      const naturalH = img.naturalHeight || viewH;

      const targetW = Math.min(naturalW, viewW * 0.95);
      const targetH = Math.min(naturalH, viewH * 0.85);

      const start = this.startRect;
      const startCenterX = start.left + start.width / 2;
      const startCenterY = start.top + start.height / 2;
      const targetCenterX = viewW / 2;
      const targetCenterY = viewH / 2;

      const dx = startCenterX - targetCenterX;
      const dy = startCenterY - targetCenterY;
      const startScale = start.width / targetW;

      this.scale = startScale;
      this.x = dx;
      this.y = dy;

      // animate to center
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.scale = 1;
          this.x = 0;
          this.y = 0;
        }, 20);
      });
    },
    playCloseAnimation() {
      const img = this.$refs.img;
      if (!this.startRect || !img) {
        this.visible = false; return;
      }
      const viewW = window.innerWidth;
      const viewH = window.innerHeight;
      const targetW = Math.min(img.naturalWidth || viewW, viewW * 0.95);
      const start = this.startRect;
      const startCenterX = start.left + start.width / 2;
      const startCenterY = start.top + start.height / 2;
      const targetCenterX = viewW / 2;
      const targetCenterY = viewH / 2;

      const dx = startCenterX - targetCenterX;
      const dy = startCenterY - targetCenterY;
      const endScale = start.width / targetW;

      this.scale = 1;
      this.x = 0;
      this.y = 0;

      // animate to thumbnail
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.scale = endScale;
          this.x = dx;
          this.y = dy;
        }, 20);
      });

      // hide after animation and notify host so it can destroy the instance
      setTimeout(() => { this.visible = false; this.resetTransforms(); this.$emit('closed'); }, 250);
    },
    resetTransforms() { this.scale = 1; this.x = 0; this.y = 0; },
    toggleZoom() { this.scale = this.scale > 1 ? 1 : 2; this.x = 0; this.y = 0; },
    onWheel(e) {
      const delta = -e.deltaY || e.wheelDelta;
      const factor = delta > 0 ? 1.1 : 0.9;
      this.scale = Math.max(1, Math.min(this.maxZoom, this.scale * factor));
    },
    startPan(e) {
      this.isPanning = true;
      this.lastPointer = { x: e.clientX, y: e.clientY };
      window.addEventListener('mousemove', this.onPan);
      window.addEventListener('mouseup', this.endPan);
    },
    onPan(e) {
      if (!this.isPanning) return;
      const dx = e.clientX - this.lastPointer.x;
      const dy = e.clientY - this.lastPointer.y;
      this.x += dx; this.y += dy;
      this.lastPointer = { x: e.clientX, y: e.clientY };
    },
    endPan() { this.isPanning = false; window.removeEventListener('mousemove', this.onPan); window.removeEventListener('mouseup', this.endPan); },
    onTouchStart(e) {
      if (e.touches.length === 2) {
        const d = this.dist(e.touches[0], e.touches[1]);
        this.pinchStartDist = d; this.pinchStartScale = this.scale;
      } else if (e.touches.length === 1) {
        this.lastPointer = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    },
    onTouchMove(e) {
      if (e.touches.length === 2) {
        const d = this.dist(e.touches[0], e.touches[1]);
        const factor = d / this.pinchStartDist;
        this.scale = Math.max(1, Math.min(this.maxZoom, this.pinchStartScale * factor));
      } else if (e.touches.length === 1 && this.scale > 1) {
        const t = e.touches[0];
        const dx = t.clientX - this.lastPointer.x; const dy = t.clientY - this.lastPointer.y;
        this.x += dx; this.y += dy; this.lastPointer = { x: t.clientX, y: t.clientY };
      }
    },
    onTouchEnd() { this.lastPointer = null; this.pinchStartDist = 0; this.pinchStartScale = 1; }
    ,dist(a,b){ const dx=a.clientX-b.clientX; const dy=a.clientY-b.clientY; return Math.sqrt(dx*dx+dy*dy); }
  }
};
</script>

<style scoped>
.iv-thumb{display:inline-block;cursor:zoom-in}
.iv-overlay{position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center}
.iv-backdrop{position:absolute;inset:0;background:rgba(0,0,0,0.8);transition:opacity .25s}
.iv-stage{position:relative;max-width:95vw;max-height:85vh;display:flex;align-items:center;justify-content:center}
.iv-image{max-width:100%;max-height:100%;will-change:transform;transition:transform .25s ease;max-width: 70vh;max-height: 70vh;border-radius: 10px;}
.iv-close{position:absolute;top:18px;right:18px;background:rgba(0,0,0,0.4);color:#fff;border:none;padding:8px 10px;border-radius:8px;font-size:18px}

/* Fade transition for overlay */
.iv-fade-enter-active,.iv-fade-leave-active{transition:opacity .25s}
.iv-fade-enter,.iv-fade-leave-to{opacity:0}
@media (max-width: 600px) {
.iv-image{
  max-width: 500px;
}
}
@media (max-width: 420px) {
.iv-image{
  max-width: 300px;
}
}

</style>
